import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { Box, Card, Container, Grid, Tab, Tabs } from '@mui/material';
import { TabsContainerWrapper } from 'src/layouts/TabsContainerWrapper';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import routes from 'src/Resources/routes.json';
import InstallationSearch from './InstallationSearch';
import { FormattedMessage } from 'react-intl';
import { UserContext } from '../../../contexts/userContext';
import { InstallationsContext } from '../../../contexts/InstallationsContextProvider';
import ListIcon from '@mui/icons-material/List';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TreeView from '../Tree/treeView';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';
import { UserType } from '../../../interfaces/UserTypes';
import SodioHomeInstallation from './Installation';
import AcknowledgementDialog, { CURRENT_TERMS_VERSION } from '../../../components/AcknowledgementDialog';
import axiosConfig from '../../../Resources/axiosConfig';

interface SodioHomeInstallationTabsProps {
  product: number;
}

function SodioHomeInstallationTabs(props: SodioHomeInstallationTabsProps) {
  const location = useLocation();
  const context = useContext(UserContext);
  const { currentUser, setUser } = context;

  const showTermsDialog =
    currentUser?.acknowledgedTermsVersion == null ||
    currentUser.acknowledgedTermsVersion < CURRENT_TERMS_VERSION;

  const handleAcknowledgeTerms = () => {
    axiosConfig
      .put('/AcknowledgeTerms', undefined, {
        params: { version: CURRENT_TERMS_VERSION }
      })
      .then(() => {
        const updatedUser = {
          ...currentUser,
          acknowledgedTermsVersion: CURRENT_TERMS_VERSION
        };
        setUser(updatedUser);
      });
  };
  const tabList = [
    'live',
    'overview',
    'batteryview',
    'information',
    'manage',
    'log',
    'history',
    'configuration',
    'report',
    'installationTickets'
  ];

  const [currentTab, setCurrentTab] = useState<string>(undefined);
  const [fetchedInstallations, setFetchedInstallations] =
    useState<boolean>(false);
  const {
    sodiohomeInstallations,
    sodistoreProInstallations,
    fetchAllInstallations,
    socket,
    openSocket,
    closeSocket
  } = useContext(InstallationsContext);
  const { product, setProduct } = useContext(ProductIdContext);
  const baseRoute = props.product === 5 ? routes.sodistorepro_installations : routes.sodiohome_installations;
  const installations = props.product === 5 ? sodistoreProInstallations : sodiohomeInstallations;

  useEffect(() => {
    let path = location.pathname.split('/');

    if (path[path.length - 2] === 'list') {
      setCurrentTab('list');
      fetchAllInstallations(product, false);
    } else if (path[path.length - 2] === 'tree') {
      setCurrentTab('tree');
    } else {
      //Even if we are located at path: /batteryview/mainstats, we want the BatteryViewSalidomo tab to be bold
      setCurrentTab(path.find((pathElement) => tabList.includes(pathElement)));
    }
  }, [location]);

  useEffect(() => {
    setProduct(props.product);
  }, [props.product]);

  useEffect(() => {
    if (product == props.product) {
      fetchAllInstallations(product);
    }
  }, [product]);

  const handleTabsChange = (_event: ChangeEvent<{}>, value: string): void => {
    setCurrentTab(value);
  };

  const navigateToTabPath = (pathname: string, tab_value: string): string => {
    let pathlist = pathname.split('/');
    let ret_path = '';
    for (let i = 1; i < pathlist.length; i++) {
      if (Number.isNaN(Number(pathlist[i]))) {
        ret_path += '/';
        ret_path += pathlist[i];
      } else {
        //When finding the installation id (number), break and then add the tab_value
        ret_path += '/';
        ret_path += pathlist[i];
        ret_path += '/';
        break;
      }
    }

    ret_path += tab_value;
    return ret_path;
  };

  const singleInstallationTabs =
    currentUser.userType == UserType.admin
      ? [
          {
            value: 'live',
            label: <FormattedMessage id="live" defaultMessage="Live" />
          },
          {
            value: 'overview',
            label: <FormattedMessage id="overview" defaultMessage="Overview" />
          },
          {
            value: 'batteryview',
            label: (
              <FormattedMessage
                id="batteryview"
                defaultMessage="Battery View"
              />
            )
          },
          {
            value: 'log',
            label: <FormattedMessage id="log" defaultMessage="Log" />
          },
          {
            value: 'manage',
            label: (
              <FormattedMessage
                id="manage"
                defaultMessage="Access Management"
              />
            )
          },
          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          },
          {
            value: 'configuration',
            label: (
              <FormattedMessage
                id="configuration"
                defaultMessage="Configuration"
              />
            )
          },
          {
            value: 'history',
            label: (
              <FormattedMessage
                id="history"
                defaultMessage="History Of Actions"
              />
            )
          },
          {
            value: 'report',
            label: (
              <FormattedMessage
                id="report"
                defaultMessage="Report"
              />
            )
          },
          {
            value: 'installationTickets',
            label: <FormattedMessage id="tickets" defaultMessage="Tickets" />
          }
        ]
      : currentUser.userType == UserType.partner
      ? [
          {
            value: 'live',
            label: <FormattedMessage id="live" defaultMessage="Live" />
          },
          {
            value: 'overview',
            label: <FormattedMessage id="overview" defaultMessage="Overview" />
          },
          {
            value: 'batteryview',
            label: (
              <FormattedMessage
                id="batteryview"
                defaultMessage="Battery View"
              />
            )
          },
          {
            value: 'log',
            label: <FormattedMessage id="log" defaultMessage="Log" />
          },
          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          },
          {
            value: 'report',
            label: (
              <FormattedMessage
                id="report"
                defaultMessage="Report"
              />
            )
          }
        ]
      : [
          {
            value: 'live',
            label: <FormattedMessage id="live" defaultMessage="Live" />
          },
          {
            value: 'overview',
            label: <FormattedMessage id="overview" defaultMessage="Overview" />
          },
          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          },
          {
            value: 'report',
            label: (
              <FormattedMessage
                id="report"
                defaultMessage="Report"
              />
            )
          }
        ];

  const inInstallationView =
    currentTab != 'list' &&
    currentTab != 'tree' &&
    !location.pathname.includes('folder');

  // Determine if current installation is Growatt (device=3) to hide report tab
  const currentInstallation = installations.find((i) =>
    location.pathname.includes(`/${i.id}/`)
  );
  const isGrowatt = currentInstallation?.device === 3
    || (installations.length === 1 && installations[0].device === 3);

  const tabs = inInstallationView && currentUser.userType == UserType.admin
      ? [
          {
            value: 'list',
            icon: <ListIcon id="mode-toggle-button-list-icon" />
          },
          {
            value: 'tree',
            icon: <AccountTreeIcon id="mode-toggle-button-tree-icon" />
          },
          {
            value: 'live',
            label: <FormattedMessage id="live" defaultMessage="Live" />
          },
          {
            value: 'overview',
            label: <FormattedMessage id="overview" defaultMessage="Overview" />
          },
          {
            value: 'batteryview',
            label: (
              <FormattedMessage
                id="batteryview"
                defaultMessage="Battery View"
              />
            )
          },
          {
            value: 'log',
            label: <FormattedMessage id="log" defaultMessage="Log" />
          },
          {
            value: 'manage',
            label: (
              <FormattedMessage
                id="manage"
                defaultMessage="Access Management"
              />
            )
          },
          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          },
          {
            value: 'configuration',
            label: (
              <FormattedMessage
                id="configuration"
                defaultMessage="Configuration"
              />
            )
          },
          {
            value: 'history',
            label: (
              <FormattedMessage
                id="history"
                defaultMessage="History Of Actions"
              />
            )
          },
          {
            value: 'report',
            label: (
              <FormattedMessage
                id="report"
                defaultMessage="Report"
              />
            )
          },
          {
            value: 'installationTickets',
            label: <FormattedMessage id="tickets" defaultMessage="Tickets" />
          }
        ]
      : inInstallationView && currentUser.userType == UserType.partner
      ? [
          {
            value: 'list',
            icon: <ListIcon id="mode-toggle-button-list-icon" />
          },
          {
            value: 'tree',
            icon: <AccountTreeIcon id="mode-toggle-button-tree-icon" />
          },
          {
            value: 'live',
            label: <FormattedMessage id="live" defaultMessage="Live" />
          },
          {
            value: 'overview',
            label: <FormattedMessage id="overview" defaultMessage="Overview" />
          },
          {
            value: 'batteryview',
            label: (
              <FormattedMessage
                id="batteryview"
                defaultMessage="Battery View"
              />
            )
          },
          {
            value: 'log',
            label: <FormattedMessage id="log" defaultMessage="Log" />
          },
          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          },
          {
            value: 'report',
            label: (
              <FormattedMessage
                id="report"
                defaultMessage="Report"
              />
            )
          }
        ]
      : inInstallationView && currentUser.userType == UserType.client
      ? [
          {
            value: 'list',
            icon: <ListIcon id="mode-toggle-button-list-icon" />
          },
          {
            value: 'tree',
            icon: <AccountTreeIcon id="mode-toggle-button-tree-icon" />
          },
          {
            value: 'live',
            label: <FormattedMessage id="live" defaultMessage="Live" />
          },
          {
            value: 'overview',
            label: <FormattedMessage id="overview" defaultMessage="Overview" />
          },
          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          },
          {
            value: 'report',
            label: (
              <FormattedMessage
                id="report"
                defaultMessage="Report"
              />
            )
          }
        ]
      : [
          {
            value: 'list',
            icon: <ListIcon id="mode-toggle-button-list-icon" />
          },
          {
            value: 'tree',
            icon: <AccountTreeIcon id="mode-toggle-button-tree-icon" />
          }
        ];

  return installations.length > 1 ? (
    <>
      <AcknowledgementDialog
        open={showTermsDialog}
        onAcknowledge={handleAcknowledgeTerms}
      />
      <Container maxWidth="xl" sx={{ marginTop: '20px' }} className="mainframe">
        <TabsContainerWrapper>
          <Tabs
            onChange={handleTabsChange}
            value={currentTab}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            {tabs
              .filter((tab) => !(isGrowatt && tab.value === 'report'))
              .map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                icon={tab.icon}
                component={Link}
                label={tab.label}
                id={`tour-tab-${tab.value}`}
                to={
                  tab.value === 'list' || tab.value === 'tree'
                    ? routes[tab.value]
                    : navigateToTabPath(location.pathname, routes[tab.value])
                }
              />
            ))}
          </Tabs>
        </TabsContainerWrapper>
        <Card variant="outlined">
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="stretch"
            spacing={0}
          >
            <Routes>
              <Route
                path={routes.list + '*'}
                element={
                  <Grid item xs={12}>
                    <Box p={4}>
                      <InstallationSearch
                        installations={installations}
                        product={props.product}
                      />
                    </Box>
                  </Grid>
                }
              />

              <Route path={routes.tree + '*'} element={<TreeView />} />

              <Route
                path={'*'}
                element={
                  <Navigate
                    to={baseRoute + routes.list}
                  ></Navigate>
                }
              ></Route>
            </Routes>
          </Grid>
        </Card>
      </Container>
    </>
  ) : installations.length === 1 ? (
    <>
      <AcknowledgementDialog
        open={showTermsDialog}
        onAcknowledge={handleAcknowledgeTerms}
      />
      <Container maxWidth="xl" sx={{ marginTop: '20px' }} className="mainframe">
        <TabsContainerWrapper>
          <Tabs
            onChange={handleTabsChange}
            value={currentTab}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            {singleInstallationTabs
              .filter((tab) => !(isGrowatt && tab.value === 'report'))
              .map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                component={Link}
                label={tab.label}
                to={routes[tab.value]}
                id={`tour-tab-${tab.value}`}
              />
            ))}
          </Tabs>
        </TabsContainerWrapper>
        <Card variant="outlined">
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="stretch"
            spacing={0}
          >
            <Routes>
              <Route
                path={'*'}
                element={
                  <Grid item xs={12}>
                    <Box p={4}>
                      <SodioHomeInstallation
                        current_installation={installations[0]}
                        type="installation"
                      ></SodioHomeInstallation>
                    </Box>
                  </Grid>
                }
              />
            </Routes>
          </Grid>
        </Card>
      </Container>
    </>
  ) : null;
}

export default SodioHomeInstallationTabs;
